import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import WorkspaceLayout from '@/components/WorkspaceLayout';
import { PARTNER_NAV } from '@/lib/workspaceNav';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, CalendarRange, ShieldCheck, CheckCircle2, Clock, AlertTriangle,
  School, Users, FileText, ClipboardCheck, ArrowRight, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from '@/pages/coordinator/AssignMentorDialog';
import { Stat, PageHeader, PROGRAM_STATUS, OPEN_OFFER_STATES } from './shared';
import SafeguardingContactCard from '@/components/SafeguardingContactCard';

const today = () => new Date().toISOString().slice(0, 10);

export default function PartnerDashboard() {
  const { pathname } = useLocation();
  const tab = pathname.includes('/programs') ? 'programs'
    : pathname.includes('/company') ? 'company' : 'overview';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/partner/overview');
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 404) setData({ noCompany: true });
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <WorkspaceLayout nav={PARTNER_NAV} activeRole="partner" testId="partner-dashboard">
      {loading ? (
        <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>
      ) : data?.noCompany ? (
        <p className="py-20 text-center text-sm text-[#9CA3AF] max-w-sm mx-auto leading-relaxed" data-testid="partner-no-company">
          No company is linked to this account yet. Open the invitation link your school sent you to get started.
        </p>
      ) : tab === 'overview' ? <Overview data={data} />
        : tab === 'programs' ? <Programs data={data} />
          : <CompanyProfile data={data} onSaved={load} />}
    </WorkspaceLayout>
  );
}

/* ───────────────────────── Overview: the daily action centre ───────────────────────── */

function Overview({ data }) {
  const [interns, setInterns] = useState(null);
  const [shortlist, setShortlist] = useState(null);

  useEffect(() => {
    api.get('/partner/interns').then(r => setInterns(r.data)).catch(() => setInterns({ groups: [] }));
    api.get('/partner/shortlisted').then(r => setShortlist(r.data)).catch(() => setShortlist({ groups: [] }));
  }, []);

  const programsById = useMemo(
    () => Object.fromEntries((data.programs || []).map(p => [p.id, p])), [data.programs]);

  const actions = useMemo(() => {
    if (!interns || !shortlist) return null;
    const now = today();
    const attendance = [];
    const feedback = [];

    (interns.groups || []).forEach(g => {
      // a closed programme is read-only, so it can never produce a genuine action
      if ((programsById[g.program_id]?.status || 'draft') === 'closed') return;
      g.opportunities.forEach(o => {
        const active = o.students.filter(s => !s.withdrawn);
        (o.days || []).filter(d => d <= now).forEach(day => {
          const missing = active.filter(s => !s.attendance.days.find(x => x.day === day)?.status).length;
          if (missing > 0) {
            attendance.push({
              key: `${o.id}-${day}`, programId: g.program_id, programName: g.program_name,
              offerId: o.id, offerTitle: o.title, day, count: missing, overdue: day < now,
            });
          }
        });
        const lastDay = o.joining_instructions?.reporting_end_date || o.end_date;
        if (o.awaiting_feedback > 0 && lastDay && lastDay < now) {
          feedback.push({
            key: o.id, programId: g.program_id, programName: g.program_name,
            offerId: o.id, offerTitle: o.title, count: o.awaiting_feedback, endedOn: lastDay,
          });
        }
      });
    });

    attendance.sort((a, b) => (a.overdue === b.overdue ? a.day.localeCompare(b.day) : a.overdue ? -1 : 1));

    const decisions = (shortlist.groups || [])
      .filter(g => programsById[g.program_id]?.partner_selects
        && (programsById[g.program_id]?.status || 'draft') !== 'closed')
      .map(g => ({
        key: g.program_id, programId: g.program_id, programName: g.program_name,
        count: g.opportunities.reduce((n, o) => n + (o.pending || 0), 0),
      }))
      .filter(g => g.count > 0);

    const offers = (data.offers || [])
      .filter(o => OPEN_OFFER_STATES.includes(o.status) || o.review_state === 'pending_reapproval')
      .map(o => ({
        key: o.id, offerId: o.id, programId: o.program_id, programName: o.program_name,
        title: o.title || 'Untitled offer',
        what: o.status === 'awaiting_company' ? 'Needs your confirmation'
          : o.status === 'changes_requested' ? 'The school asked for changes'
            : o.status === 'draft' ? 'Draft not submitted yet' : 'Changes with the school',
      }));

    const missingOffers = (data.programs || [])
      .filter(p => p.accepting_offers && !(data.offers || []).some(o => o.program_id === p.id))
      .map(p => ({ key: `new-${p.id}`, programId: p.id, programName: p.name, newOffer: true,
        what: `No offer yet · due ${formatDay(p.application_deadline)}` }));

    return { attendance, feedback, decisions, offers: [...offers, ...missingOffers] };
  }, [interns, shortlist, data.offers, data.programs, programsById]);

  const decisionGroups = (shortlist?.groups || [])
    .filter(g => programsById[g.program_id]?.partner_selects
      && (programsById[g.program_id]?.status || 'draft') !== 'closed')
    .map(g => ({ ...g, pending: g.opportunities.reduce((m, o) => m + (o.pending || 0), 0) }));

  const byStatus = (s) => (data.programs || []).filter(p => (p.status || 'draft') === s).length;

  const firstName = (data.company?.rep_name || '').replace(/^(Dr\.?|Mr\.?|Mrs\.?|Ms\.?|Prof\.?)\s+/i, '').split(' ')[0] || 'there';
  const nothing = actions && !actions.attendance.length && !actions.feedback.length
    && !actions.decisions.length && !actions.offers.length;

  return (
    <div data-testid="partner-overview">
      <PageHeader title={`Hello, ${firstName}!`}
        subtitle={`${data.company?.name} · ${data.programs?.[0]?.school_name || 'your school'}`} />

      <div className="grid grid-cols-3 gap-3 mb-7">
        <Stat label="Total programmes" value={data.programs?.length || 0} testId="stat-programmes"
          to="/partner/programs?status=all" />
        <Stat label="Open programmes" value={byStatus('open')} testId="stat-open-programmes"
          to="/partner/programs?status=open" />
        <Stat label="Closed programmes" value={byStatus('closed')} testId="stat-closed-programmes"
          to="/partner/programs?status=closed" />
      </div>

      <h2 className="text-base font-semibold text-[#1F2937] mb-2.5">Needs your attention</h2>

      {!actions ? (
        <div className="py-12 text-center" data-testid="actions-loading"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#9CA3AF]" /></div>
      ) : nothing ? (
        <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-10 text-center" data-testid="nothing-pending">
          <CheckCircle2 className="h-7 w-7 text-emerald-200 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#1F2937] font-['Outfit']">All clear</p>
          <p className="text-sm text-[#9CA3AF] mt-1">
            Nothing is waiting on you. Attendance and completion notes appear here as soon as they are due.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="needs-attention">
          {actions.attendance.map(a => (
            <ActionRow key={a.key}
              testId={`action-attendance-${a.offerId}-${a.day}`}
              tone={a.overdue ? 'urgent' : 'normal'}
              icon={a.overdue ? AlertTriangle : CalendarRange}
              title={`${a.overdue ? 'Overdue attendance' : 'Attendance'} · ${formatDay(a.day)}`}
              detail={`${a.programName} · ${a.offerTitle} · ${a.count} intern${a.count === 1 ? '' : 's'} to mark`}
              cta="Mark attendance"
              to={`/partner/programs/${a.programId}?tab=interns&attendance=${a.offerId}&day=${a.day}`} />
          ))}

          {actions.feedback.map(f => (
            <ActionRow key={f.key}
              testId={`action-feedback-${f.offerId}`}
              icon={ClipboardCheck}
              title="Completion feedback pending"
              detail={`${f.programName} · ${f.offerTitle} · ${f.count} student${f.count === 1 ? '' : 's'} · ended ${formatDay(f.endedOn)}`}
              cta="Submit feedback"
              to={`/partner/programs/${f.programId}?tab=interns&feedback=${f.offerId}`} />
          ))}

          {actions.decisions.map(d => (
            <ActionRow key={d.key}
              testId={`action-decisions-${d.programId}`}
              icon={Users}
              title="Student decisions pending"
              detail={`${d.programName} · ${d.count} shortlisted student${d.count === 1 ? '' : 's'} awaiting your decision`}
              cta="Review students"
              to={`/partner/programs/${d.programId}?tab=shortlisted`} />
          ))}

          {actions.offers.map(o => (
            <ActionRow key={o.key}
              testId={`action-offer-${o.offerId || o.programId}`}
              icon={FileText}
              title={o.newOffer ? 'Internship offer needed' : o.title}
              detail={`${o.programName} · ${o.what}`}
              cta={o.newOffer ? 'Create offer' : 'Open offer'}
              to={o.newOffer ? `/partner/offers/new?program=${o.programId}` : `/partner/offers/${o.offerId}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionRow({ icon: Icon, title, detail, cta, to, tone = 'normal', testId }) {
  return (
    <div className={`bg-white rounded-2xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between sm:flex-wrap gap-3 transition-colors ${tone === 'urgent' ? 'border-[#F97D6A]/50 bg-[#FFF8F7]' : 'border-[#E5E7EB]'}`}
      data-testid={testId}>
      <div className="flex items-start gap-2.5 min-w-0">
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${tone === 'urgent' ? 'text-[#F06651]' : 'text-[#9CA3AF]'}`} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1F2937]">{title}</p>
          <p className="text-xs sm:text-[11px] text-[#6B7280] mt-0.5 break-words">{detail}</p>
        </div>
      </div>
      <Button asChild className={`rounded-lg text-xs shrink-0 w-full sm:w-auto h-11 sm:h-8 ${tone === 'urgent' ? 'bg-[#F97D6A] text-white hover:bg-[#F06651]' : 'bg-[#1F2937] text-white hover:bg-[#374151]'}`}>
        <Link to={to} className="flex items-center justify-center" data-testid={`${testId}-cta`}>
          {cta}<ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Link>
      </Button>
    </div>
  );
}

/* ───────────────────────── Programmes ───────────────────────── */

function Programs({ data }) {
  const [params, setParams] = useSearchParams();
  const raw = params.get('status');
  const status = ['all', 'open', 'closed', 'draft'].includes(raw) ? raw : 'all';
  const setStatus = (v) => setParams({ status: v }, { replace: true });
  const programs = (data.programs || []).filter(p => status === 'all' || (p.status || 'draft') === status);
  const offerCount = (id) => (data.offers || []).filter(o => o.program_id === id).length;

  return (
    <div data-testid="partner-programs">
      <PageHeader title="Internship programmes"
        subtitle={`Everything your organisation runs with ${data.programs?.[0]?.school_name || 'your school'}.`} />

      <div className="flex gap-1.5 mb-4 flex-wrap" data-testid="program-status-filter">
        {[['all', 'All'], ['open', 'Open'], ['closed', 'Closed'], ['draft', 'Draft']].map(([f, label]) => (
          <button key={f} onClick={() => setStatus(f)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${status === f ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200 hover:border-[#9CA3AF]'}`}
            data-testid={`program-filter-${f}`}>{label}</button>
        ))}
      </div>

      {programs.length === 0 ? (
        <p className="text-sm text-[#9CA3AF] py-10 text-center" data-testid="no-programs">
          {status === 'all' ? 'No invitations yet. Schools invite companies by email.' : 'No programmes with this status.'}
        </p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {programs.map(p => {
            const st = PROGRAM_STATUS[p.status] || PROGRAM_STATUS.draft;
            return (
              <Link key={p.id} to={`/partner/programs/${p.id}`}
                className="bg-white rounded-2xl border border-[#E5E7EB] p-5 hover:border-[#F97D6A]/50 hover:shadow-sm transition-all duration-200 block"
                data-testid={`program-card-${p.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <School className="h-3.5 w-3.5 text-[#F97D6A] shrink-0" />
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#9CA3AF] truncate">{p.school_name}</p>
                  </div>
                  <Badge className={`border-0 text-[10px] ${st.cls}`} data-testid={`program-card-status-${p.id}`}>{st.label}</Badge>
                </div>
                <p className="text-base font-semibold text-[#1F2937] font-['Outfit']">{p.name}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5 text-[11px] text-[#4B5563]">
                  <span className="flex items-center gap-1"><CalendarRange className="h-3 w-3 text-[#9CA3AF]" />{formatDay(p.start_date)} – {formatDay(p.end_date)}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-[#9CA3AF]" />Offers by {formatDay(p.application_deadline)}</span>
                </div>
                <div className="flex items-center gap-4 mt-3.5 text-[11px] text-[#6B7280]">
                  <span data-testid={`program-card-offers-${p.id}`}>{offerCount(p.id)} offer{offerCount(p.id) === 1 ? '' : 's'}</span>
                  <span data-testid={`program-card-interns-${p.id}`}>{p.confirmed_interns ?? '—'} confirmed intern{p.confirmed_interns === 1 ? '' : 's'}</span>
                  <span className="ml-auto text-[#F97D6A] font-semibold inline-flex items-center gap-1">View programme<ArrowRight className="h-3 w-3" /></span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Company profile ───────────────────────── */

function CompanyProfile({ data, onSaved }) {
  const c = data.company || {};
  const [form, setForm] = useState({
    name: c.name || '', industry: c.industry || '', website: c.website || '',
    address: c.address || '', registration_number: c.registration_number || '',
    rep_name: c.rep_name || '', rep_phone: c.rep_phone || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/partner/company', form);
      toast.success('Company profile saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the profile'); }
    setSaving(false);
  };

  const acceptDeclaration = async () => {
    try {
      await api.post('/partner/declaration');
      toast.success('Declaration accepted');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record the declaration'); }
  };

  const inputCls = 'mt-1.5 rounded-xl h-11 border-gray-200';
  return (
    <div className="max-w-3xl" data-testid="partner-company">
      <PageHeader title="Company profile" subtitle="Schools see these details alongside your internship offers." />

      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 space-y-4" data-testid="company-information">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="h-4 w-4 text-[#F97D6A]" />
          <h2 className="text-base font-semibold text-[#1F2937]">Company information</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Registered company name *</Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} className={inputCls} data-testid="company-name" />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Industry</Label>
            <Input value={form.industry} onChange={e => set('industry', e.target.value)} placeholder="e.g. Architecture" className={inputCls} data-testid="company-industry" />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Website</Label>
            <Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://" className={inputCls} data-testid="company-website" />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Registration number</Label>
            <Input value={form.registration_number} onChange={e => set('registration_number', e.target.value)} placeholder="If available" className={inputCls} data-testid="company-registration" />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Representative name</Label>
            <Input value={form.rep_name} onChange={e => set('rep_name', e.target.value)} className={inputCls} data-testid="company-rep-name" />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Representative phone</Label>
            <Input value={form.rep_phone} onChange={e => set('rep_phone', e.target.value)} className={inputCls} data-testid="company-rep-phone" />
          </div>
        </div>
        <div>
          <Label className="text-sm font-medium text-[#1F2937]">Business address</Label>
          <Textarea value={form.address} onChange={e => set('address', e.target.value)} className="mt-1.5 rounded-xl border-gray-200 min-h-[70px] resize-none" data-testid="company-address" />
        </div>
        <p className="text-[11px] text-[#9CA3AF]">
          Authorised representative: <strong className="text-[#4B5563]">{c.rep_email}</strong> — offers for this
          company can only be managed from this email address.
        </p>
        <Button onClick={save} disabled={saving} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold" data-testid="save-company-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save profile
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 mt-4" data-testid="declaration-card">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="h-4 w-4 text-[#F97D6A]" />
          <h2 className="text-base font-semibold text-[#1F2937]">Partner declaration</h2>
        </div>
        <ul className="space-y-1.5 mt-3">
          {data.declaration.points.map(p => (
            <li key={p} className="text-sm text-[#4B5563] flex items-start gap-2 leading-relaxed">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-px" />{p}
            </li>
          ))}
        </ul>
        {data.declaration.needs_acceptance ? (
          <div className="mt-4">
            {data.declaration.accepted && (
              <p className="text-[11px] text-amber-600 mb-2" data-testid="declaration-outdated">
                You accepted an earlier version ({data.declaration.accepted.version}) on{' '}
                {formatDay(data.declaration.accepted.accepted_at?.slice(0, 10))}. The declaration has
                since been updated and needs accepting again.
              </p>
            )}
            <Button onClick={acceptDeclaration} variant="outline" className="rounded-xl text-xs border-gray-200" data-testid="accept-declaration-btn">
              Accept the declaration
            </Button>
          </div>
        ) : (
          <p className="text-[11px] text-emerald-600 mt-3 flex items-center gap-1.5" data-testid="declaration-accepted">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Accepted by {data.declaration.accepted.accepted_by} on {formatDay(data.declaration.accepted.accepted_at?.slice(0, 10))}
            <span className="text-[#9CA3AF]">({data.declaration.accepted.version})</span>
          </p>
        )}
      </div>

      <div className="mt-4">
        <SafeguardingContactCard />
      </div>
    </div>
  );
}
